import React, { ReactNode } from 'react';
import find from 'lodash/find';
import { v4 as uuidv4 } from 'uuid';

import { toMonth } from 'Common/src/utils/Birthday';

import { TAppAddress, TAppContact, TAppContactId, TAppDate, TAppProvenance } from 'TProtocol/common/models';
import styled from 'styled-components';

const daySuffixes: string[] = ['th', 'st', 'nd', 'rd'];

export const toDateString = (contact: TAppContact): string => {
  let str = '';
  if (contact.birthday) {
    if (contact.birthday.month && contact.birthday.month.length > 0) {
      str = toMonth(contact.birthday.month) + ' ';
    }
    if (contact.birthday.day && contact.birthday.day.length > 0) {
      str += contact.birthday.day;
    }
    if (contact.birthday.year && contact.birthday.year.length > 0) {
      if (str !== '') {
        str += ', ';
      }
      str += contact.birthday.year;
    }
  }
  return str;
};

export const toBirthdayString = (contact: TAppContact): string => {
  let str = '';
  const today: Date = localDate();
  if (contact.birthday && contact.birthday.month && contact.birthday.month.length > 0
    && contact.birthday.day && contact.birthday.day.length > 0) {
    const day: number = parseInt(contact.birthday.day);
    const month: number = parseInt(contact.birthday.month);
    if (today.getDate() === day && today.getMonth() === (month - 1)) {
      str = 'Today 🎂 Happy Birthday!';
    } else {
      str = toMonth(contact.birthday.month) + ' ' + day + ' 🎂';
    }

    let year = 1;
    if (contact.birthday.year && contact.birthday.year.length === 4) {
      year = parseInt(contact.birthday.year);
      const birthdayDate: Date = new Date(year, month - 1, day);
      const age = getAge(birthdayDate);
      const suffix = ageSuffix(age);
      if (today.getDate() === birthdayDate.getDate() && today.getMonth() === birthdayDate.getMonth()) {
        str = 'Today 🎂 Happy ' + age + suffix + ' Birthday!';
      } else {
        str += ' turning ' + age;
      }
    }
  }
  return str;
};

export const ageSuffix = (age: number): string => {
  if (age > 10 && age < 21) {
    return daySuffixes[0];
  } else if (age % 10 === 1) {
    return daySuffixes[1];
  } else if (age % 10 === 2) {
    return daySuffixes[2];
  } else if (age % 10 === 3) {
    return daySuffixes[3];
  } else {
    return daySuffixes[0];
  }
};

export const getAge = (birthday: Date): number => {
  const today: Date = localDate();
  const diffYear: number = today.getFullYear() - birthday.getFullYear();
  if (today.getDate() === birthday.getDate() && today.getMonth() === birthday.getMonth()) {
    // Birthday is today, so just return as is.
    return diffYear;
  } else if (today.getMonth() == birthday.getMonth() && today.getDate() > birthday.getDate()) {
    // Birthday has just passed in current month, add a year.
    return diffYear + 1;
  } else if (today.getMonth() > birthday.getMonth()) {
    // Birthday is in previous month, add a year.
    return diffYear + 1;
  } else {
    // Birthday has not occurred yet
    return diffYear;
  }
};

export const isValidBirthday = (contact: TAppContact): boolean => {
  return !!(contact.birthday && contact.birthday.month && contact.birthday.month.length > 0
    && contact.birthday.day && contact.birthday.day.length > 0);
};

export const isEmptyDate = (date?: TAppDate): boolean => {
  return (!date || (
    (date.year === undefined || date.year === '') &&
    (date.month === undefined || date.month === '') &&
    (date.day === undefined || date.day === '')));
}

export const isValidDate = (date?: TAppDate): boolean => {
  if (!date) {
    return false;
  }
  const year = date.year ? +date.year : 1980; // random leap year to test.
  const month = date.month ? +date.month - 1 : undefined;
  const day = date.day ? +date.day : undefined;

  if (month === undefined || day === undefined) {
    return false;
  }

  const testDate = new Date(year, month, day);
  return testDate.getFullYear() === year && testDate.getMonth() === month && testDate.getDate() === day;
};

export const localDate = (): Date => {
  return new Date(Date.now());
};

export const getAddressString = (address: TAppAddress): string => {
  let addStr = '';
  if (address.address1) {
    addStr = address.address1;
  }

  if (address.address2 && address.address1) {
    addStr += ', ' + address.address2;
  } else if (address.address2) {
    addStr += address.address2;
  }

  if ((address.city && address.address1) || (address.city && address.address2)) {
    addStr += ', ' + address.city;
  } else if (address.city) {
    addStr += address.city;
  }

  if (((address.admin1 && address.address1) || (address.admin1 && address.address2)) && (address.city)) {
    addStr += ' ' + address.admin1;
  } else if (((address.admin1 && address.address1) || (address.admin1 && address.address2)) && (!address.city)) {
    addStr += ', ' + address.admin1;
  } else if ((address.admin1 && address.city)) {
    addStr += ' ' + address.admin1;
  }

  if (address.postalCode && !address.city && !address.admin1 && (address.address1 || address.address2)) {
    addStr += ', ' + address.postalCode;
  } else if (address.postalCode) {
    addStr += ' ' + address.postalCode;
  }

  if ((address.country && (address.address1 || address.address2)) && !address.city && !address.admin1 && !address.postalCode) {
    addStr += ', ' + address.country;
  } else if (address.country) {
    addStr += ' ' + address.country;
  }
  return addStr;
};

export const multiLineAddressString = (address: TAppAddress, combineAddress2 = false): ReactNode => {
  const addressNodes: ReactNode[] = [];

  if (address.address2 && combineAddress2) {
    addressNodes.push(
      <div key="address">{address.address1} <Address2Inline key="address2-inline">{address.address2}</Address2Inline>
      </div>
    );
  } else if (address.address2) {
    addressNodes.push(
      <div key="address1">{address.address1} <Address2Inline>{address.address2}</Address2Inline></div>,
      <Address2SecondLine key="address2">{address.address2}</Address2SecondLine>
    );
  } else if (address.address1) {
    addressNodes.push(
      <div key="address1">{address.address1}</div>
    );
  }

  const cityStateZip = [];
  if (address.city) {
    cityStateZip.push(address.city);
  }

  if (address.admin1) {
    if (address.city) {
      cityStateZip.push(', ');
    }
    cityStateZip.push(address.admin1);
  }

  if (address.postalCode) {
    cityStateZip.push(' ');
    cityStateZip.push(address.postalCode);
  }
  if (cityStateZip.length) {
    addressNodes.push(
      <div key="city-state-zip">{cityStateZip.join('')}</div>
    );
  }

  if (address.country) {
    addressNodes.push(
      <div key="country">{address.country}</div>
    );
  }

  return addressNodes;
};

export const appleMapsAddressQueryString = (address?: TAppAddress): string => {
  if (!address) {
    return '';
  }
  return [
    address.address1,
    address.address2,
    address.city,
    address.admin1,
    address.postalCode,
    address.country
  ].filter((term): term is string => !!term)
    .map(encodeURIComponent).join(',');
};

export const getContactName = (contact: TAppContact): string => {
  if (!contact.name) {
    return '';
  }
  let name = '';
  if (contact.name.firstName && contact.name.firstName.length > 0) {
    name = contact.name.firstName;
  }
  if (contact.name.lastName && contact.name.lastName.length > 0) {
    if (contact.name.firstName && contact.name.firstName.length > 0) {
      name += ' ';
    }
    name += contact.name.lastName;
  }
  if (name === '') {
    name = contact.name.fullName || '';
  }
  return name;
};

export const incrementClientEditSeqId = (contact: TAppContact): void => {
  const clientEditSeqIds = find(contact.originalContactIds, { uuid: contact.contactId })?.clientEditSeqIds;
  if (clientEditSeqIds) {
    const existingSeqId = clientEditSeqIds.get(TAppProvenance.MOUSE);
    clientEditSeqIds.set(TAppProvenance.MOUSE, (existingSeqId ?? 0) + 1);
  } else {
    if (!contact.originalContactIds) {
      contact.originalContactIds = [];
    }

    const ids = new Map<TAppProvenance, number>();
    ids.set(TAppProvenance.MOUSE, 1);
    contact.originalContactIds.push(new TAppContactId({
      uuid: uuidv4(),
      clientEditSeqIds: ids
    }));
  }
};

export const Address2Inline = styled.span`
  display: inline-block;
`;
export const Address2SecondLine = styled.div`
  overflow: clip;
`;
